if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this._idx   = 0;
        this._total = 0;
      }

      show(opener) {
        super.show(opener);
        this._setupCarousel();
        this.showActiveMedia();
      }

      hide() {
        this._stopAllMedia();
        super.hide();
      }

      // ── One-time carousel setup ──────────────────────────────────
      _setupCarousel() {
        if (this._carouselReady) return;
        this._carouselReady = true;

        this._track   = this.querySelector('.pmm-track');
        this._slides  = Array.from(this.querySelectorAll('.pmm-slide'));
        this._dots    = Array.from(this.querySelectorAll('.pmm-dot'));
        this._counter = this.querySelector('[id^="PmmCurrent-"]');
        this._total   = this._slides.length;

        if (!this._track || this._total === 0) return;

        // Prevent Dawn's pointerup-close on carousel clicks
        const body = this.querySelector('.pmm-body');
        if (body) body.addEventListener('pointerup', (e) => e.stopPropagation());

        // Arrow buttons
        const prev = this.querySelector('.pmm-prev');
        const next = this.querySelector('.pmm-next');
        if (prev) prev.addEventListener('click', () => this._navigate(-1));
        if (next) next.addEventListener('click', () => this._navigate(1));

        // Dot buttons
        this._dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this._goTo(i));
        });

        // Keyboard ← →
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft')  this._navigate(-1);
          if (e.key === 'ArrowRight') this._navigate(1);
        });

        // Touch swipe — intentionally ignores pinch-zoom (multi-touch) gestures.
        // A swipe is only registered when:
        //   1. Exactly one finger was on the screen at touchstart (not a pinch)
        //   2. Horizontal movement exceeds 80px (raised from 40px to avoid
        //      triggering on accidental drift during a pinch attempt)
        //   3. Horizontal movement is greater than vertical movement
        //      (so a diagonal pinch/spread doesn't mis-fire as a swipe)
        let _startX = 0;
        let _startY = 0;
        let _isSingleTouch = false;
        this._track.addEventListener('touchstart', (e) => {
          // Only track as a potential swipe if exactly one finger is down.
          // Two or more fingers = pinch-zoom intent → do not register.
          // Also fires when a second finger is added mid-gesture, which
          // cancels the in-progress single-touch swipe tracking.
          _isSingleTouch = e.touches.length === 1;
          if (_isSingleTouch) {
            _startX = e.touches[0].clientX;
            _startY = e.touches[0].clientY;
          }
        }, { passive: true });
        this._track.addEventListener('touchend', (e) => {
          if (!_isSingleTouch) return; // was a multi-touch gesture, ignore
          const dx = _startX - e.changedTouches[0].clientX;
          const dy = _startY - e.changedTouches[0].clientY;
          // Navigate only if horizontal travel > 80px AND dominates vertical travel
          if (Math.abs(dx) > 80 && Math.abs(dx) > Math.abs(dy)) {
            this._navigate(dx > 0 ? 1 : -1);
          }
        }, { passive: true });
      }

      // ── Jump to the slide matching the clicked media ─────────────
      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        if (!mediaId) { this._goTo(0, false); return; }
        const slide = this.querySelector(`.pmm-slide[data-media-id="${mediaId}"]`);
        const idx   = slide ? parseInt(slide.dataset.index, 10) : 0;
        this._goTo(idx, false);
      }

      // ── Navigate with wraparound ─────────────────────────────────
      _navigate(dir) {
        let next = this._idx + dir;
        if (next < 0)            next = this._total - 1;
        if (next >= this._total) next = 0;
        this._goTo(next);
      }

      // ── Move to a specific slide ─────────────────────────────────
      _goTo(idx, animate = true) {
        if (!this._track) return;

        // Pause media in the slide we're leaving
        this._pauseSlideMedia(this._slides[this._idx]);

        this._idx = idx;
        this._track.style.transition = animate
          ? 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)'
          : 'none';
        this._track.style.transform = `translateX(-${idx * 100}%)`;

        this._dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
        if (this._counter) this._counter.textContent = idx + 1;

        // Play media in the new slide after transition completes
        const delay = animate ? 340 : 0;
        setTimeout(() => this._playSlideMedia(this._slides[idx]), delay);
      }

      // ── Play media in a slide ────────────────────────────────────
      _playSlideMedia(slide) {
        if (!slide) return;
        const type = slide.dataset.mediaType;

        if (type === 'video') {
          const video = slide.querySelector('video');
          if (!video) return;
          video.play().catch(() => {
            // Browser blocked unmuted autoplay — retry muted
            video.muted = true;
            video.play().catch(() => {});
          });
        }

        if (type === 'external_video') {
          const container = slide.querySelector('.pmm-ext-video');
          if (!container) return;
          // If iframe already injected (e.g. navigated back), use postMessage
          const existing = container.querySelector('iframe');
          if (existing) {
            this._postPlayMessage(existing, container.dataset.host);
          } else {
            this._injectExternalVideo(container);
          }
        }
      }

      // ── Pause media in a slide ───────────────────────────────────
      _pauseSlideMedia(slide) {
        if (!slide) return;
        const video = slide.querySelector('video');
        if (video && !video.paused) video.pause();

        const iframe = slide.querySelector('iframe');
        if (iframe) {
          const host = slide.querySelector('.pmm-ext-video')?.dataset.host;
          this._postPauseMessage(iframe, host);
        }
      }

      // ── Stop all media (on modal close) ─────────────────────────
      _stopAllMedia() {
        this.querySelectorAll('video').forEach((v) => {
          v.pause();
          v.currentTime = 0;
        });
        // Remove external video iframes — they'll be re-created on next open
        this.querySelectorAll('.pmm-ext-video').forEach((c) => {
          const iframe = c.querySelector('iframe');
          if (iframe) iframe.remove();
          // Restore poster + play icon
          const host    = c.dataset.host;
          const videoId = c.dataset.videoId;
          if (!c.querySelector('.pmm-ext-poster') && videoId) {
            c.innerHTML = `<img class="pmm-ext-poster" src="" alt=""><div class="pmm-play-icon"></div>`;
          }
        });
      }

      // ── External video helpers ───────────────────────────────────
      _injectExternalVideo(container) {
        const host    = container.dataset.host;
        const videoId = container.dataset.videoId;
        let src = '';

        if (host === 'youtube') {
          src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&enablejsapi=1`;
        } else if (host === 'vimeo') {
          src = `https://player.vimeo.com/video/${videoId}?autoplay=1&api=1`;
        }
        if (!src) return;

        const iframe = document.createElement('iframe');
        iframe.src             = src;
        iframe.className       = 'pmm-iframe';
        iframe.allow           = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.setAttribute('loading', 'lazy');
        container.innerHTML = '';
        container.appendChild(iframe);
      }

      _postPlayMessage(iframe, host) {
        if (host === 'youtube') {
          iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else if (host === 'vimeo') {
          iframe.contentWindow?.postMessage('{"method":"play"}', '*');
        }
      }

      _postPauseMessage(iframe, host) {
        if (host === 'youtube') {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (host === 'vimeo') {
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        }
      }
    }
  );
}